import { ActionContext, Module } from 'vuex';
import {
  DocIdCommitId,
  DocumentComponentsState,
  RootState,
  CommitId,
  HistoryNestedMap, PrevCurrNextCommitId,
} from '@/utils/types/store';
import {
  createDocumentParagraphs,
  DocumentComponent,
  DocumentParagraph,
} from '@/utils/types/documentRepo/documentComponent';
import helpers from '@/store/helpers';
import api from '@/config/api';
import {
  ComponentVersion,
  DocumentHistory,
  DocumentVersion,
} from '@/utils/types/documentRepo/history';
import functions from '@/utils/functions';
import names from './names';

type Ctx = ActionContext<DocumentComponentsState, RootState>;

const initialState: DocumentComponentsState = {
  // History of document repo (map of componentId to ComponentVersion, by DocumentRepoCommit id)
  documentHistoryMap: {} as HistoryNestedMap,
  // DocumentParagraphs by DocumentRepoCommit id
  documentParagraphsByCommitId: {} as Record<CommitId, DocumentParagraph[]>,
  // Index of selected DocumentParagraph
  selectedParagraphIndex: undefined,
  // Selected DocumentParagraph
  currParagraph: undefined,
  // Previous DocumentParagraph in history (from currentParagraph)
  prevParagraph: undefined,
  // Next DocumentParagraph in history (from currentParagraph)
  nextParagraph: undefined,
};

interface CommitIdAndParagraphs {
  commitId: string;
  paragraphs: DocumentParagraph[];
}

interface PrevCurrNextParagraphs {
  prev?: DocumentParagraph;
  curr: DocumentParagraph;
  next?: DocumentParagraph;
}

const documentComponentsModule: Module<DocumentComponentsState, RootState> = {
  namespaced: true,
  state: initialState,
  mutations: {
    [names.mutations._SAVE_DOCUMENT_REPO_HISTORY](state, documentRepoHistory: DocumentHistory) {
      const historyMap: HistoryNestedMap = functions.asRecord(
        documentRepoHistory.documentVersions,
        (v: DocumentVersion) => (v.documentRepoCommitId),
        (v: DocumentVersion) => functions.asRecord(
          v.componentVersions,
          (cv: ComponentVersion) => (cv._id),
        ),
      );
      state.documentHistoryMap = historyMap;
    },
    [names.mutations._SAVE_DOCUMENT_REPO_COMMIT_COMPONENTS](state, cs: CommitIdAndParagraphs) {
      const { commitId, paragraphs } = cs;
      state.documentParagraphsByCommitId = {
        ...state.documentParagraphsByCommitId,
        [commitId]: paragraphs,
      };
    },
    [names.mutations._SAVE_SELECTED_COMPONENT_INDEX](state, index: number | undefined) {
      state.selectedParagraphIndex = index;
    },
    [names.mutations._SAVE_PREV_CURR_NEXT_PARAGRAPHS](state, paragraphs: PrevCurrNextParagraphs) {
      const { prev, curr, next } = paragraphs;
      state.prevParagraph = prev;
      state.currParagraph = curr;
      state.nextParagraph = next;
    },
  },
  actions: {
    loadDocumentRepoHistory({ commit }: Ctx, docId: string) {
      return helpers.axios.get(commit)(
        `${api.routes.documentRepos}/${docId}/history`,
        names.mutations._SAVE_DOCUMENT_REPO_HISTORY,
      );
    },
    loadPrevCurrNextCommitComponents({ dispatch }: Ctx, commitIds: PrevCurrNextCommitId) {
      const {
        docId, prevId, currId, nextId,
      } = commitIds;

      dispatch('clearParagraphs')
        // Load components of current commit
        .then(() => dispatch(
          'loadDocumentComponents', { docId, commitId: currId } as DocIdCommitId,
        ))

        // Load components of previous commit
        .then(() => (prevId
          ? dispatch('loadDocumentComponents', { docId, commitId: prevId } as DocIdCommitId)
          : undefined
        ))

        // Load components of next commit
        .then(() => (nextId
          ? dispatch('loadDocumentComponents', { docId, commitId: nextId } as DocIdCommitId)
          : undefined
        ));
    },
    loadDocumentComponents({ commit, state }: Ctx, docIdCommitId: DocIdCommitId) {
      const { docId, commitId } = docIdCommitId;

      // If paragraphs are already there, no need to download them again
      if (state.documentParagraphsByCommitId[commitId] !== undefined) {
        return Promise.resolve();
      }
      // Otherwise, download them
      const addCommitIdAndCreateParagraphs = (data: object | object[]) => ({
        commitId,
        paragraphs: createDocumentParagraphs(data as DocumentComponent[]),
      } as CommitIdAndParagraphs);
      return helpers.axios.get(commit)(
        `${api.routes.documentRepos}/${docId}/revisions/${commitId}/components`,
        names.mutations._SAVE_DOCUMENT_REPO_COMMIT_COMPONENTS,
        addCommitIdAndCreateParagraphs,
      );
    },
    setSelectedComponentIndex(
      {
        commit,
        dispatch,
        state,
        rootGetters,
      }: Ctx,
      index: number | undefined,
    ) {
      // Save index
      commit(names.mutations._SAVE_SELECTED_COMPONENT_INDEX, index);

      // If index is undefined, clear paragraphs and return
      if (index === undefined) {
        return dispatch('clearParagraphs');
      }

      // Get ids of current, and optionally prev and next commit
      const previousCommitId: CommitId | undefined = rootGetters['documentRepo/previousCommitId'];
      const currentCommitId: CommitId = rootGetters['documentRepo/currentCommitId'];
      const nextCommitId: CommitId | undefined = rootGetters['documentRepo/nextCommitId'];

      const currentParagraph = state.documentParagraphsByCommitId[currentCommitId][index];
      const currParagraphVersion = Object.values(state.documentHistoryMap[currentCommitId])
        .find((cv) => cv.componentId === currentParagraph._id);

      // Current paragraph has no version, store only current paragraph
      if (!currParagraphVersion) {
        const paragraphs = { curr: currentParagraph } as PrevCurrNextParagraphs;
        return Promise.resolve(commit(names.mutations._SAVE_PREV_CURR_NEXT_PARAGRAPHS, paragraphs));
      }

      // Find previous paragraph
      const prevParagraphVersionId: string | undefined = currParagraphVersion.prev?.to;
      const prevParagraphVersion: ComponentVersion | undefined = previousCommitId
      && prevParagraphVersionId
        ? state.documentHistoryMap[previousCommitId][prevParagraphVersionId]
        : undefined;
      const prevParagraphId = prevParagraphVersion?.componentId;
      const prevParagraph = previousCommitId && prevParagraphId
        ? state.documentParagraphsByCommitId[previousCommitId]
          .find((p) => p._id === prevParagraphId)
        : undefined;

      // Find next paragraph
      const nextParagraphVersionId: string | undefined = currParagraphVersion.succ?.to;
      const nextParagraphVersion: ComponentVersion | undefined = nextCommitId
      && nextParagraphVersionId
        ? state.documentHistoryMap[nextCommitId][nextParagraphVersionId]
        : undefined;
      const nextParagraphId = nextParagraphVersion?.componentId;
      const nextParagraph = nextCommitId && nextParagraphId
        ? state.documentParagraphsByCommitId[nextCommitId]
          .find((p) => p._id === nextParagraphId)
        : undefined;

      const paragraphs: PrevCurrNextParagraphs = {
        curr: currentParagraph,
        prev: prevParagraph,
        next: nextParagraph,
      };

      return Promise.resolve(commit(names.mutations._SAVE_PREV_CURR_NEXT_PARAGRAPHS, paragraphs));
    },
    clearParagraphs({ commit }: Ctx) {
      commit(names.mutations._SAVE_PREV_CURR_NEXT_PARAGRAPHS, {} as PrevCurrNextParagraphs);
    },
  },
};

export default documentComponentsModule;
